const GOOGLE_ANALYTICS_ID = "G-5XFZQ04909";
const GOOGLE_SCRIPT_ID = "eva-google-analytics";
const POSTHOG_PROJECT_KEY = "phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE";
const POSTHOG_API_HOST = "https://us.i.posthog.com";
const POSTHOG_SCRIPT_URL = "https://us-assets.i.posthog.com/static/array.js";
const POSTHOG_SCRIPT_ID = "eva-posthog-analytics";

let googleLoadPromise;
let googleConfigured = false;
let postHogLoadPromise;
let postHogConfigured = false;

function appendScript({ id, src }) {
  const existing = document.getElementById(id);
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.addEventListener("load", () => resolve(script), { once: true });
    script.addEventListener("error", () => reject(new Error(`Unable to load ${src}`)), {
      once: true,
    });
    document.head.appendChild(script);
  });
}

function ensureGoogleQueue() {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
}

export function enableGoogleAnalytics() {
  if (googleLoadPromise) return googleLoadPromise;

  ensureGoogleQueue();
  if (!googleConfigured) {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.gtag("js", new Date());
    window.gtag("config", GOOGLE_ANALYTICS_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      cookie_expires: 60 * 60 * 24 * 180,
      send_page_view: false,
    });
    googleConfigured = true;
  }

  googleLoadPromise = appendScript({
    id: GOOGLE_SCRIPT_ID,
    src: `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`,
  }).catch((error) => {
    document.getElementById(GOOGLE_SCRIPT_ID)?.remove();
    googleLoadPromise = undefined;
    throw error;
  });

  return googleLoadPromise;
}

export function trackGooglePageView(path) {
  if (!googleConfigured || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_location: window.location.href,
    page_path: path,
    page_title: document.title,
  });
}

function addPostHogMethod(target, method) {
  target[method] = function queuePostHogCall() {
    target.push([method].concat(Array.prototype.slice.call(arguments)));
  };
}

function ensurePostHogQueue() {
  if (window.posthog && window.posthog.__SV) return window.posthog;

  const posthog = window.posthog || [];
  posthog._i = posthog._i || [];
  posthog.init = function init(apiKey, config, name) {
    const target = name ? (posthog[name] = []) : posthog;
    target.people = target.people || [];
    target.toString = () => `posthog${name ? `.${name}` : ""} (stub)`;
    target.people.toString = () => `${target.toString()}.people (stub)`;

    [
      "capture",
      "identify",
      "reset",
      "opt_in_capturing",
      "opt_out_capturing",
      "has_opted_in_capturing",
      "has_opted_out_capturing",
      "set_config",
    ].forEach((method) => addPostHogMethod(target, method));

    posthog._i.push([apiKey, config, name]);
  };
  posthog.__SV = 1;
  window.posthog = posthog;
  return posthog;
}

export function enablePostHog() {
  if (postHogLoadPromise) return postHogLoadPromise;

  const posthog = ensurePostHogQueue();
  if (!postHogConfigured) {
    posthog.init(POSTHOG_PROJECT_KEY, {
      api_host: POSTHOG_API_HOST,
      person_profiles: "identified_only",
      persistence: "localStorage+cookie",
      cookie_expiration: 180,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_performance: false,
      capture_heatmaps: false,
      capture_exceptions: false,
      disable_scroll_properties: true,
      disable_session_recording: true,
      disable_surveys: true,
      disable_surveys_automatic_display: true,
      advanced_disable_feature_flags: true,
      disable_external_dependency_loading: true,
      respect_dnt: true,
    });
    postHogConfigured = true;
  }

  postHogLoadPromise = appendScript({ id: POSTHOG_SCRIPT_ID, src: POSTHOG_SCRIPT_URL }).catch(
    (error) => {
      document.getElementById(POSTHOG_SCRIPT_ID)?.remove();
      postHogLoadPromise = undefined;
      throw error;
    }
  );

  return postHogLoadPromise;
}

export function trackPostHogPageView() {
  if (!postHogConfigured || !window.posthog?.capture) return;
  window.posthog.capture("$pageview", { $current_url: window.location.href });
}

function expireCookie(name, domain) {
  const domainPart = domain ? `; domain=${domain}` : "";
  document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
}

export function clearAnalyticsStorage() {
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
  if (window.posthog?.opt_out_capturing) {
    window.posthog.opt_out_capturing();
  }

  const storageKeys = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key && key.startsWith("ph_")) storageKeys.push(key);
  }
  storageKeys.forEach((key) => window.localStorage.removeItem(key));

  const cookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name.startsWith("_ga") || name.startsWith("ph_"));
  const hostname = window.location.hostname;
  cookieNames.forEach((name) => {
    expireCookie(name);
    if (hostname) {
      expireCookie(name, hostname);
      expireCookie(name, `.${hostname}`);
    }
  });

  document.getElementById(GOOGLE_SCRIPT_ID)?.remove();
  document.getElementById(POSTHOG_SCRIPT_ID)?.remove();
}

export const analyticsScriptIds = {
  google: GOOGLE_SCRIPT_ID,
  posthog: POSTHOG_SCRIPT_ID,
};
